import { renderAdminOperations } from './16-admin-operations.mjs';
// Owns feature configuration forms. Snapshot visibility is only a UI hint;
// the server authorizes configuration reads and revision-checked writes.
/** Create release-control forms that submit server-authorized, revision-checked changes. */
export function createAdmin(ctx) {
  let generation = 0;
  function renderAdminDashboard() {
    ctx.view.append(
      ctx.button('Back to Settings', () => ctx.go('settings'), 'text-button admin-back'),
    );
    if (ctx.state?.isSuperAdmin !== true) {
      ctx.view.append(ctx.e('p', '', 'This page is available only to the server administrator.'));
      return;
    }
    ctx.view.append(ctx.e('h1', 'admin-title', 'Administrator dashboard'));
    renderFeatureAdmin();
  }
  function renderRankingAdmin(actor, current) {
    const card = ctx.e('section', 'settings-card feature-admin ranking-admin');
    const status = ctx.e('p', 'subtle', 'Loading ranking controls and aggregate measurements…');
    status.setAttribute('role', 'status');
    card.append(ctx.e('h2', '', 'Feed ranking'), status);
    ctx.view.append(card);
    const active = () =>
      current === generation &&
      card.isConnected &&
      ctx.state?.me.id === actor &&
      ctx.state?.isSuperAdmin === true;
    Promise.all([ctx.api('/api/admin/ranking'), ctx.api('/api/admin/metrics')])
      .then(([config, metrics]) => {
        if (!active()) return;
        status.textContent = `Policy ${config.policyVersion}${config.slatePolicyVersion ? ` · slots ${config.slatePolicyVersion}` : ''} · revision ${config.revision}. These weights govern blended ranking and slot backfill; dedicated slot objectives use their own versioned policy. Chronological order bypasses them.`;
        card.append(
          ctx.e(
            'p',
            'subtle',
            'Interests use declared topics, follows and opted-in public-post activity. Mood changes only through explicit feed feedback. Longer viewing is not proof of satisfaction.',
          ),
        );
        const form = ctx.e('form', 'feature-control form-stack'),
          controls = {};
        const labels = {
          mood: 'Chosen mood / tone fit',
          interests: 'Content interests',
          relationships: 'Connections',
          recency: 'Recency',
          quality: 'Content quality proxy',
        };
        for (const [key, value] of Object.entries(config.weights)) {
          const input = ctx.field('input', {
            type: 'number',
            min: 0,
            max: Math.round(config.bounds[key] * 100),
            step: 1,
            required: true,
            value: Math.round(value * 100),
          });
          controls[key] = input;
          form.append(
            ctx.append(
              ctx.e('label', 'form-stack'),
              ctx.e('span', '', `${labels[key]} (%)`),
              input,
            ),
          );
        }
        const total = ctx.e('p', 'subtle');
        const updateTotal = () => {
          total.textContent = `Total: ${Object.values(controls).reduce((sum, input) => sum + Number(input.value), 0)}% — must equal 100%.`;
        };
        form.addEventListener('input', updateTotal);
        updateTotal();
        form.append(total);
        const error = ctx.e('p', 'form-error');
        error.setAttribute('role', 'alert');
        const save = ctx.e('button', 'primary', 'Save ranking revision');
        save.type = 'submit';
        form.append(save, error);
        card.append(form);
        let request;
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          if (!active() || save.disabled) return;
          const weights = Object.fromEntries(
            Object.entries(controls).map(([key, input]) => [key, Number(input.value) / 100]),
          );
          const signature = JSON.stringify(weights);
          if (!request || request.signature !== signature)
            request = {
              signature,
              body: { revision: config.revision, weights, requestId: crypto.randomUUID() },
            };
          [...form.elements].forEach((input) => (input.disabled = true));
          error.textContent = '';
          try {
            await ctx.api('/api/admin/ranking', request.body);
            if (!active()) return;
            ctx.toast('Ranking revision saved. Refresh a feed to use it.');
            ctx.render();
          } catch (failure) {
            if (active()) error.textContent = failure.message;
          } finally {
            if (active()) [...form.elements].forEach((input) => (input.disabled = false));
          }
        });
        const summary = ctx.e('section', 'form-stack');
        summary.append(
          ctx.e('h3', '', 'Public-post viewing activity'),
          ctx.e(
            'p',
            'subtle',
            `${metrics.registeredAccounts} accounts · ${metrics.activeExposureAccounts} accounts with opted-in exposures in ${metrics.windowDays} days · ${metrics.exposures} daily post exposures.`,
          ),
          ctx.e(
            'p',
            'subtle',
            `Viewing activity measures time on content, not feed load time. ${metrics.measurement} Retention: ${metrics.retentionDays} days. No member-level activity is shown.`,
          ),
        );
        for (const [format, values] of Object.entries(metrics.formats))
          summary.append(
            ctx.e(
              'p',
              'subtle',
              `${format}: ${values.exposures} exposures · ${Math.round(values.cappedMilliseconds)} capped viewing ms.`,
            ),
          );
        card.append(summary);
        const audit = ctx.e('details', 'feature-disclosure');
        audit.append(ctx.e('summary', '', 'Ranking change history'));
        for (const entry of config.audit)
          audit.append(
            ctx.e(
              'p',
              'subtle',
              `Revision ${entry.revision} · ${new Date(entry.createdAt).toLocaleString()} · ${Object.entries(
                entry.after,
              )
                .map(([key, value]) => `${key} ${Math.round(value * 100)}%`)
                .join(' · ')}`,
            ),
          );
        if (!config.audit.length) audit.append(ctx.e('p', 'subtle', 'No ranking changes yet.'));
        card.append(audit);
      })
      .catch((error) => {
        if (active()) status.textContent = error.message;
      });
  }
  function renderFeatureAdmin() {
    if (ctx.state?.isSuperAdmin !== true) return;
    const actor = ctx.state.me.id;
    const current = ++generation;
    renderRankingAdmin(actor, current);
    renderAdminOperations(ctx);
    const card = ctx.e('section', 'settings-card feature-admin');
    const status = ctx.e('p', 'subtle', 'Loading authorized configuration…');
    status.setAttribute('role', 'status');
    card.append(ctx.e('h2', '', 'Server feature controls'), status);
    ctx.view.append(card);
    ctx
      .api('/api/admin/features')
      .then((config) => {
        if (current !== generation || !card.isConnected || ctx.state?.me.id !== actor) return;
        if (!config.flags || !config.definitions || !Number.isSafeInteger(config.revision))
          throw new Error('Feature configuration is unavailable.');
        status.textContent = `Configuration revision ${config.revision}. Dependencies and access are enforced by the server.`;
        for (const [key, rule] of Object.entries(config.flags)) {
          const disclosure = ctx.e('details', 'feature-disclosure');
          const summary = ctx.e('summary');
          summary.append(
            ctx.e('span', '', key.replaceAll('-', ' ')),
            ctx.e(
              'span',
              'feature-rule-status',
              `${rule.enabled ? 'Enabled' : 'Disabled'} · ${rule.percentage}%`,
            ),
          );
          disclosure.append(summary);
          const form = ctx.e('form', 'feature-control form-stack');
          const enabled = ctx.field('input', { type: 'checkbox', checked: rule.enabled });
          const percentage = ctx.field('input', {
            type: 'number',
            min: 0,
            max: 100,
            step: 1,
            required: true,
            value: rule.percentage,
          });
          const slider = ctx.field('input', {
            type: 'range',
            min: 0,
            max: 100,
            step: 1,
            value: rule.percentage,
            ariaLabel: `Rollout percentage for ${key}`,
          });
          slider.addEventListener('input', () => {
            percentage.value = slider.value;
          });
          percentage.addEventListener('input', () => {
            if (percentage.validity.valid) slider.value = percentage.value;
          });
          const rollout = ctx.append(ctx.e('div', 'feature-rollout'), slider, labeledPercentage());
          function labeledPercentage() {
            return ctx.append(
              ctx.e('label', 'form-stack'),
              ctx.e('span', 'sr-only', `Rollout percentage for ${key}`),
              percentage,
            );
          }
          const allow = ctx.field('textarea', { value: rule.allowUserIds.join('\n'), rows: 2 });
          const deny = ctx.field('textarea', { value: rule.denyUserIds.join('\n'), rows: 2 });
          const labeled = (text, input) =>
            ctx.append(ctx.e('label', 'form-stack'), ctx.e('span', '', text), input);
          const error = ctx.e('p', 'form-error');
          error.setAttribute('role', 'alert');
          const save = ctx.e('button', 'secondary-button', 'Save server rule');
          save.type = 'submit';
          const dependencies = config.definitions[key] ?? [];
          form.append(
            ctx.e('h3', '', key.replaceAll('-', ' ')),
            ctx.append(ctx.e('label', 'adult-confirmation'), enabled, ctx.e('span', '', 'Enabled')),
            ctx.e(
              'p',
              'subtle',
              dependencies.length
                ? `Requires: ${dependencies.join(', ')}`
                : 'No feature dependencies.',
            ),
            ctx.append(
              ctx.e('fieldset', 'feature-rollout-group'),
              ctx.e('legend', '', 'Rollout percentage (0–100)'),
              rollout,
            ),
            labeled('Allow member UUIDs (one per line)', allow),
            labeled('Deny member UUIDs (one per line)', deny),
            error,
            save,
          );
          form.addEventListener('submit', async (event) => {
            event.preventDefault();
            const targets = (value) => value.split(/[\s,]+/).filter(Boolean);
            const changed = {
              enabled: enabled.checked,
              percentage: Number(percentage.value),
              allowUserIds: targets(allow.value),
              denyUserIds: targets(deny.value),
            };
            const controls = [...form.querySelectorAll('button,input,textarea')];
            controls.forEach((control) => (control.disabled = true));
            error.textContent = '';
            try {
              await ctx.api('/api/admin/features', {
                revision: config.revision,
                flags: { [key]: changed },
              });
              await ctx.refreshState();
              ctx.toast('Server feature rule saved.');
            } catch (failure) {
              error.textContent = failure.message;
              if (failure.status === 409)
                error.append(ctx.button('Reload server configuration', ctx.render));
            } finally {
              controls.forEach((control) => (control.disabled = false));
            }
          });
          disclosure.append(form);
          card.append(disclosure);
        }
        const history = ctx.e('details', 'feature-audit');
        history.append(ctx.e('summary', '', 'Recent server audit'));
        for (const entry of config.audit ?? [])
          history.append(
            ctx.e(
              'p',
              'subtle',
              `Revision ${entry.revision} · ${new Date(entry.createdAt).toLocaleString()} · ${Object.keys(entry.after ?? {}).join(', ')}`,
            ),
          );
        if (!config.audit?.length)
          history.append(ctx.e('p', 'subtle', 'No feature changes recorded.'));
        card.append(history);
      })
      .catch((error) => {
        if (card.isConnected && ctx.state?.me.id === actor) {
          status.textContent = error.message;
          card.append(ctx.button('Retry configuration', ctx.render));
        }
      });
  }
  return { renderFeatureAdmin, renderAdminDashboard };
}
